import { Component, EventEmitter, HostListener, Output, ViewChild } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { buttonInOut } from 'animations-lib';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FieldMetaTypeGuards } from '../../../interfaces/FieldMetaTypeGuards';
import { EntityTypeLabelPair, IFieldMeta } from '../../../interfaces/imetadata';
import { FieldProviderService, ValidFieldsData } from '../../../services/field-provider.service';
import { TipControllerService } from '../../../services/tip-controller.service';
import { FieldConfig } from '../../../utils/fieldconfig';
import { FieldType } from '../../../utils/fieldtype';
import { FilterableItem, IFilterableItem } from '../../filterable-list/filterable-item';
import { FilterableListComponent } from '../../filterable-list/filterable-list.component';
import { IBreadcrumbPath, IBreadcrumbPathItem } from '../breadcrumbs/breadcrumbs.component';
import { IReInkable } from '../ireinkable';
import { IValidStateChangeProvider, ValidStateProvider } from '../valid-state-provider';
import { FieldConfigChangedControllerService } from './field-config-changed-controller.service';

@Component({
    selector: 'app-data-fields',
    templateUrl: './data-fields.component.html',
    styleUrls: ['../field-selection.component.scss', '../field-config/field-config.component.scss'],
    animations: [buttonInOut],
    standalone: false
})
export class DataFieldsComponent implements IValidStateChangeProvider, IReInkable {
  @Output()
  fieldInsertedEvent = new EventEmitter<{
    entity: EntityTypeLabelPair;
    field: IFieldMeta;
  }>();

  @Output()
  fieldUpdatedEvent = new EventEmitter<{
    entity: EntityTypeLabelPair;
    field: IFieldMeta;
    fieldConfig: FieldConfig;
  }>();

  @ViewChild('repeatable')
  repeatableFieldFilterableListComponent!: FilterableListComponent;

  @ViewChild('fields')
  fieldsFieldFilterableListComponent!: FilterableListComponent;

  repeatableItems: IFilterableItem[] = [];
  fieldItems: IFilterableItem[] = [];
  focusedFieldActionFields: IFieldMeta[] | undefined;
  fieldIsConfigurable = false;
  fieldConfig: FieldConfig = {};
  optionsOpen = false;
  validFieldData: ValidFieldsData | undefined;
  breadcrumbs: IBreadcrumbPath | undefined;
  selectedTabIndex = 0;
  protected modified = false;

  valid = new ValidStateProvider();

  private toOneFilter = new ToggleFilter((f: IFieldMeta) => f.type === 'to-one', true);
  private toManyFilter = new ToggleFilter((f: IFieldMeta) => f.type === 'to-many', true);
  private compositeFilter = new ToggleFilter((f: IFieldMeta) => f.type === 'composite', true);
  private showShortCuts = false;

  @ViewChild(MatTabGroup) tabGroup: MatTabGroup | undefined;

  constructor(
    private tipControllerService: TipControllerService,
    private configChangedControllerService: FieldConfigChangedControllerService,
    fieldProviderService: FieldProviderService
  ) {
    fieldProviderService.validFieldsSubjectChanged$.subscribe((validFields) => {
      this.configChangedControllerService.clear();
      this.updateOnValidFields(validFields);
    });

    this.configChangedControllerService.optionsChanged$.subscribe((changed) => {
      this.modified = changed;
    });
  }

  get validStateChange$(): Observable<boolean> {
    return this.valid.validStateChange$;
  }

  get isRepeatableField(): boolean {
    return !!this.validFieldData?.fields?.length;
  }

  get isLeaf(): boolean {
    return this.validFieldData?.fields?.length === 0;
  }

  get isLeafWithOptions(): boolean {
    return this.isLeaf && !this.fieldIsConfigurable;
  }

  get isLeafWithoutOptions(): boolean {
    return this.isLeaf && this.fieldIsConfigurable;
  }

  reInkMatTab(): void {
    setTimeout(() => {
      this.tabGroup?.realignInkBar();
    }, 0);
  }

  breadcrumbSelected(bc: IBreadcrumbPathItem): void {
    this.setupFilterableLists(this.validFieldData!.fields, bc.id);
    this.autoSelectTab();
  }

  openConfig(event: Event): void {
    this.optionsOpen = true;
    event.stopPropagation();
  }

  closeOptions(): void {
    this.optionsOpen = false;
  }

  onUpdateClick() {
    if (this.validFieldData) {
      this.fieldUpdatedEvent.emit({
        entity: this.validFieldData.entity,
        field: this.validFieldData.activeField!,
        fieldConfig: this.validFieldData.activeConfig,
      });
      this.closeOptions();
      this.configChangedControllerService.clear(); // applying the options means no changes anymore
    }
  }

  onFieldInserted(item: IFilterableItem, srcComponent: FilterableListComponent): void {
    this.fireFieldInserted(item.data);

    const clearComponent =
      srcComponent === this.repeatableFieldFilterableListComponent
        ? this.fieldsFieldFilterableListComponent
        : this.repeatableFieldFilterableListComponent;
    clearComponent.clearSelection();
  }

  private updateOnValidFields(validFieldsData: ValidFieldsData | undefined) {
    this.validFieldData = validFieldsData;

    if (!validFieldsData) {
      this.breadcrumbs = undefined;
      this.valid.setValid(false);
      return;
    }

    const activeField = validFieldsData.activeField;
    this.setupFilterableLists(validFieldsData.fields, activeField?.path);
    this.autoSelectTab();

    this.breadcrumbs = this.buildBreadcrumbs(validFieldsData);
    this.fieldIsConfigurable = this.getFieldConfigurable(activeField?.type);
    this.focusedFieldActionFields = validFieldsData.fields?.filter((f) => f.type !== 'to-many'); // for sort/conditional fields
    this.fieldConfig = validFieldsData.activeConfig;
    this.optionsOpen = this.fieldIsConfigurable && this.validFieldData?.fields?.length === 0;

    this.valid.setValid(true);
  }

  private setupFilterableLists(fields: IFieldMeta[], path: string | undefined) {
    this.assertFieldsBelongToPath(fields, path);

    const isHidden = (f: IFieldMeta) => {
      const isContainer = f.type === 'to-many' || f.type === 'to-one' || f.type === 'composite';
      const isParentPickerField = f.parent !== undefined && FieldMetaTypeGuards.isBullhorn(f.parent) &&
        f.parent.isPickerField;
      const isPickerField = FieldMetaTypeGuards.isBullhorn(f) && f.isPickerField;
      return isParentPickerField && (isContainer || isPickerField);
    };

    const isAssociatedField = (f: IFieldMeta) => f.type === 'to-many' || f.type === 'to-one' || f.type === 'composite';
    this.repeatableItems =
      fields
        .filter(
          (f) =>
            !isHidden(f) &&
            isAssociatedField(f) &&
            (this.toOneFilter.filter(f) || this.toManyFilter.filter(f) || this.compositeFilter.filter(f))
        )
        .map((f) => FilterableItem.fromFieldMeta(f)) || [];
    this.fieldItems = fields.filter((f) => !isAssociatedField(f)).map((f) => FilterableItem.fromFieldMeta(f)) || [];
  }

  private autoSelectTab(): void {
    if (this.fieldItems.length > 0) {
      this.selectedTabIndex = 1;
    } else {
      this.selectedTabIndex = 0;
    }
  }

  private buildBreadcrumbs(validFieldsData: ValidFieldsData): IBreadcrumbPath {
    if (!validFieldsData.activeField) {
      return {
        pathItems: [
          {
            id: validFieldsData.entity.entity,
            label: validFieldsData.entity.label,
          },
        ],
      };
    }

    const breadcrumbs: IBreadcrumbPath = { pathItems: [] };
    const isSelectablePath = (path: string) => validFieldsData.paths.find((p) => p === path) !== undefined;

    // all paths to active field
    let field: IFieldMeta | undefined = validFieldsData.activeField;
    while (field) {
      const disabled = !isSelectablePath(field.path);
      breadcrumbs.pathItems.push({
        id: field.path,
        label: field.label,
        disabled: disabled,
      });
      field = field.parent;
    }

    breadcrumbs.pathItems.push({
      id: validFieldsData.entity.entity,
      label: validFieldsData.entity.label,
      disabled: !isSelectablePath(validFieldsData.entity.entity),
    });

    breadcrumbs.tooltip = breadcrumbs.pathItems.length > 0 ? breadcrumbs.pathItems[0].id : undefined;

    breadcrumbs.pathItems.reverse();

    return breadcrumbs;
  }

  private fireFieldInserted(field: IFieldMeta): void {
    this.fieldInsertedEvent.emit({
      entity: this.validFieldData!.entity,
      field: field,
    });
  }

  private getFieldConfigurable(fieldType: string | undefined): boolean {
    return (
      fieldType === FieldType.DOUBLE ||
      fieldType === FieldType.INTEGER ||
      fieldType === FieldType.DATE ||
      fieldType === FieldType.STRING ||
      fieldType === FieldType.BOOLEAN ||
      fieldType === FieldType.TO_MANY
    );
  }

  @HostListener('document:keydown.alt.1', ['$event'])
  @HostListener('document:keydown.alt.2', ['$event'])
  @HostListener('document:keydown.alt.3', ['$event'])
  @HostListener('document:keydown.alt.k', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.code === 'KeyK') {
      this.showShortCuts = !this.showShortCuts;
      this.tipControllerService.set(
        this.showShortCuts
          ? [
              'Alt + 1 toggle to-one.',
              'Alt + 2 toggle to-many.',
              'Alt + 3 toggle composite.',
              'Alt + k shortcut keys.',
              'Alt + Shift + v version.',
              'Alt + Shift + / info.',
            ]
          : undefined
      );
      return;
    }

    if (event.code === 'Digit1') {
      this.toOneFilter.toggle();
    } else if (event.code === 'Digit2') {
      this.toManyFilter.toggle();
    } else if (event.code === 'Digit3') {
      this.compositeFilter.toggle();
    }

    const tips = [];
    tips.push(`Show to-one (${this.toOneFilter.on ? 'ON' : 'OFF'})`);
    tips.push(`Show to-many (${this.toManyFilter.on ? 'ON' : 'OFF'})`);
    tips.push(`Show composite (${this.compositeFilter.on ? 'ON' : 'OFF'})`);
    tips.push('Alt + k, Alt + k back.');
    this.tipControllerService.set(tips);

    this.setupFilterableLists(this.validFieldData!.fields, undefined);
  }

  // check to see if we've started using this for something unexpected
  private assertFieldsBelongToPath(fields: IFieldMeta[], path: string | undefined): void {
    if (environment.production) {
      return;
    }

    if (!path) {
      return;
    }

    const pathToField = (field: IFieldMeta) => {
      const lastDotIndex = field.path.lastIndexOf('.');
      return lastDotIndex < 0 ? field.path : field.path.substr(0, lastDotIndex);
    };

    for (const field of fields) {
      if (pathToField(field) !== path) {
        throw Error(`Fields detected that do not belong to path '${path}'.`);
      }
    }
  }
}

export class ToggleFilter {
  private readonly _filter: (f: IFieldMeta) => boolean;
  private _on = false;

  constructor(filterFunction: (f: IFieldMeta) => boolean, initialStateOn?: boolean) {
    this._filter = (f: IFieldMeta) => this._on && filterFunction(f);
    this._on = !!initialStateOn;
  }

  get filter(): (f: IFieldMeta) => boolean {
    return this._filter;
  }

  get on(): boolean {
    return this._on;
  }

  toggle(): void {
    this._on = !this._on;
  }
}
